// src/components/Modal/UploadUserImages.jsx
import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuthContext } from "../../context";
import {
  userImageURL,
  userImageDeleteURL,
} from "../../api/api";

const MAX_SIZE_MB = 3;

const UploadUserImages = ({ onUploadSuccess, onClose }) => {
  const { user, token, fetchUser } = useAuthContext();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [deletingUrl, setDeletingUrl] = useState(null);

  // Robustly parse `user.image` into an array:
  let existing = [];
  if (user?.image) {
    let imgs = user.image;
    if (typeof imgs === "string") {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    if (typeof imgs === "string") {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    if (Array.isArray(imgs)) {
      existing = imgs;
    }
  }

  const maxCount = user.membership_status ? 6 : 3;

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.some((f) => f.size > MAX_SIZE_MB * 1024 * 1024)) {
      return toast.error(`Each file must be ≤ ${MAX_SIZE_MB}MB`);
    }
    if (existing.length + selected.length > maxCount) {
      return toast.error(
        `You can only have up to ${maxCount} images. You already have ${existing.length}.`
      );
    }
    setFiles(selected);
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      return toast.error("Please select images to upload.");
    }
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    setUploading(true);
    try {
      const res = await axios.post(
        `${userImageURL}?type=gallery`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("Uploaded!");

      // Close modal & fire upload-success callback immediately
      onUploadSuccess?.(res.data);
      onClose?.();

      // Refresh the user in the background, but don't await it
      fetchUser().catch(console.error);
    } catch (err) {
          // If the server responded with 413, it's because the payload was too large
     if (err.response?.status === 413) {
       toast.error(`One of the files is too big (max ${MAX_SIZE_MB} MB).`);
     } else {
        toast.error(err.response?.data?.message || "Upload failed.");
     }
    } finally {
      setUploading(false);
    }
  };


  const handleDelete = async (url) => {
    if (!window.confirm("Delete this image?")) return;
    setDeletingUrl(url);
    try {
      await axios.delete(userImageDeleteURL, {
        headers: { Authorization: `Bearer ${token}` },
        data: { urlsToRemove: [url] },
      });
      toast.success("Deleted!");
      await fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed.");
    } finally {
      setDeletingUrl(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-start overflow-y-auto pt-12 pb-12">
      <div className="bg-white rounded-md w-[90%] max-w-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold">Manage Images</h3>

        {/* Existing images */}
        {existing.length > 0 && (
          <div className="grid grid-cols-3 gap-3">
            {existing.map((url) => (
              <div key={url} className="relative">
                <img
                  src={url}
                  alt=""
                  className="w-full h-24 object-contain rounded"
                />
                <button
                  onClick={() => handleDelete(url)}
                  disabled={deletingUrl === url}
                  className="absolute top-1 right-1 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* File input */}
        <div>
          <input
            type="file"
            multiple
            accept="image/png, image/jpeg"
            onChange={handleFileChange}
            className="block w-full text-sm text-gray-600"
          />
          <p className="text-xs text-gray-500 mt-1">
            {`Max ${maxCount} images total, each ≤ ${MAX_SIZE_MB}MB`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 rounded hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading}
            className={`px-4 py-2 rounded text-white ${
              uploading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadUserImages;
